/**
 * Chat Search Database Utilities
 *
 * Functions for searching chats and messages, including semantic search
 * capabilities with vector embeddings.
 *
 * Primary responsibilities:
 * - Text-based chat/message search
 * - Semantic chat/message search
 * - Managing message embeddings
 *
 * Dependencies:
 * - Supabase
 * - OpenAI/local embeddings
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import { generateLocalEmbedding } from '@/lib/generate-local-embedding'
import { isDev } from '@/lib/utils/isDev'
import { Tables } from '@/supabase/types'
import type { Database } from '@/supabase/types'
import {
  canUseOpenAIEmbeddings,
  createOpenAIEmbeddingsClient,
  OPENAI_EMBEDDING_MODEL
} from '@/lib/portkey-embeddings'
import { getProfileByUserId } from './profile'
import { extractInteractiveComponentsFromContent } from '@/lib/interactive-components'
import type { MessageMetadata } from '@/types/messages'

/** Use service client on server when no client passed; browser client only on client. */
function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()
  return require('@/lib/supabase/browser-client').supabase as SupabaseClient<Database>
}

export interface ChatSearchResult {
  chat_id: string
  message_id: string
  content: string
  role: string
  created_at: string
  sequence_number: number
  chat_name: string
  similarity: number
}

/**
 * Adds embeddings to a message for semantic search
 */
export async function addEmbeddingsToMessage(
  messageId: string,
  content: string,
  userId: string,
  client?: SupabaseClient<Database>
): Promise<boolean> {
  try {
    const db = getDefaultClient(client)
    // Generate local embedding first
    const localEmbedding = await generateLocalEmbedding(content)

    // Try to generate OpenAI embedding if user has API key
    const updateData: {
      local_embedding: number[]
      openai_embedding?: number[]
      metadata?: MessageMetadata
    } = {
      local_embedding: localEmbedding
    }

    try {
      const profile = await getProfileByUserId(userId)

      if (profile && canUseOpenAIEmbeddings(profile)) {
        const openai = createOpenAIEmbeddingsClient(profile)
        const response = await openai.embeddings.create({
          model: OPENAI_EMBEDDING_MODEL,
          input: content
        })

        updateData.openai_embedding = response.data[0].embedding
      }
    } catch (err) {
      isDev && console.error('Error generating OpenAI embedding for message:', err)
      // Continue with just local embedding
    }

    // Compute interactive components and merge into metadata
    try {
      const { data: messageRow, error: fetchError } = await db
        .from('messages')
        .select('metadata, chat_id, created_at')
        .eq('id', messageId)
        .single()

      if (fetchError) {
        isDev && console.error('Error fetching message for interactive components:', fetchError)
      } else if (messageRow) {
        const existingMetadata = (messageRow.metadata as MessageMetadata | null) ?? undefined

        const interactiveComponents = extractInteractiveComponentsFromContent(
          content,
          messageId,
          messageRow.chat_id,
          messageRow.created_at
        )

        let mergedMetadata: MessageMetadata | undefined = existingMetadata

        if (interactiveComponents.length > 0) {
          mergedMetadata = {
            ...(existingMetadata ?? {}),
            interactiveComponents
          }
        } else if (existingMetadata && 'interactiveComponents' in existingMetadata) {
          // Remove stale interactiveComponents if they are no longer present
          // while preserving other metadata fields
          const { interactiveComponents: _ignored, ...rest } = existingMetadata
          mergedMetadata = rest as MessageMetadata
        }

        if (mergedMetadata) {
          updateData.metadata = mergedMetadata
        }
      }
    } catch (err) {
      isDev &&
        console.error('Error extracting interactive components for message metadata:', err)
    }

    // Update the message with embeddings and metadata
    const { error } = await db.from('messages').update(updateData).eq('id', messageId)

    if (error) {
      isDev && console.error('Error updating message with embeddings:', error)
      return false
    }

    return true
  } catch (error) {
    isDev && console.error('Error in addEmbeddingsToMessage:', error)
    return false
  }
}

/**
 * Search chats by text query (non-semantic)
 */
export async function searchChatsByText(
  workspaceId: string,
  query: string,
  client?: SupabaseClient<Database>
): Promise<Tables<'chats'>[]> {
  try {
    if (!workspaceId || !query.trim()) {
      return []
    }

    const db = getDefaultClient(client)

    const { data: chats, error } = await db.rpc('search_chats_and_messages', {
      p_workspace_id: workspaceId,
      p_query: query
    })

    if (error) {
      isDev && console.error('Error searching chats by text:', error)
      return []
    }

    return chats || []
  } catch (error) {
    isDev && console.error('Error in searchChatsByText:', error)
    return []
  }
}

/**
 * Search messages semantically
 */
export async function searchChatsSemanticLocal(
  userId: string,
  embedding: number[],
  limit = 10,
  threshold = 0.5,
  client?: SupabaseClient<Database>
): Promise<ChatSearchResult[]> {
  try {
    const db = getDefaultClient(client)

    const { data, error } = await db.rpc('match_messages_local', {
      user_id_param: userId,
      query_embedding: embedding,
      match_count: limit,
      similarity_threshold: threshold
    })

    if (error) {
      isDev && console.error('Error searching messages with local embeddings:', error)
      throw error
    }

    // Format results to include chat info
    const formattedResults: ChatSearchResult[] = []

    for (const result of data || []) {
      // Get chat info
      const { data: chatData } = await db
        .from('chats')
        .select('name')
        .eq('id', result.chat_id)
        .single()

      formattedResults.push({
        chat_id: result.chat_id,
        message_id: result.id,
        content: result.content,
        role: result.role,
        created_at: result.created_at,
        sequence_number: result.sequence_number,
        chat_name: chatData?.name || 'Unknown Chat',
        similarity: result.similarity
      })
    }

    return formattedResults
  } catch (error) {
    isDev && console.error('Error in searchChatsSemanticLocal:', error)
    return []
  }
}

/**
 * Search messages semantically with OpenAI embeddings
 */
export async function searchChatsSemanticOpenAI(
  userId: string,
  embedding: number[],
  limit = 10,
  threshold = 0.5,
  client?: SupabaseClient<Database>
): Promise<ChatSearchResult[]> {
  try {
    const db = getDefaultClient(client)

    const { data, error } = await db.rpc('match_messages_openai', {
      user_id_param: userId,
      query_embedding: embedding,
      match_count: limit,
      similarity_threshold: threshold
    })

    if (error) {
      isDev && console.error('Error searching messages with OpenAI embeddings:', error)
      throw error
    }

    // Format results to include chat info
    const formattedResults: ChatSearchResult[] = []

    for (const result of data || []) {
      // Get chat info
      const { data: chatData } = await db
        .from('chats')
        .select('name')
        .eq('id', result.chat_id)
        .single()

      formattedResults.push({
        chat_id: result.chat_id,
        message_id: result.id,
        content: result.content,
        role: result.role,
        created_at: result.created_at,
        sequence_number: result.sequence_number,
        chat_name: chatData?.name || 'Unknown Chat',
        similarity: result.similarity
      })
    }

    return formattedResults
  } catch (error) {
    isDev && console.error('Error in searchChatsSemanticOpenAI:', error)
    return []
  }
}

/**
 * Optimized semantic search function that combines both approaches
 * and returns results directly with chat information
 */
export async function searchChatsSemantic(
  userId: string,
  query: string,
  limit = 10,
  threshold = 0.5,
  client?: SupabaseClient<Database>
): Promise<ChatSearchResult[]> {
  try {
    const db = getDefaultClient(client)

    // Get user profile for OpenAI API key
    const profile = await getProfileByUserId(userId)

    // Generate embeddings
    const localEmbedding = await generateLocalEmbedding(query)
    let openaiEmbedding: number[] | null = null

    // Try OpenAI embeddings if available
    if (profile && canUseOpenAIEmbeddings(profile)) {
      try {
        const openai = createOpenAIEmbeddingsClient(profile)
        const response = await openai.embeddings.create({
          model: OPENAI_EMBEDDING_MODEL,
          input: query
        })

        openaiEmbedding = response.data[0].embedding
      } catch (err) {
        isDev && console.error('Error using OpenAI embeddings for search:', err)
        // Fall back to local embeddings
      }
    }

    // Use the optimized combined search function
    const { data, error } = await db.rpc('semantic_search_chats', {
      user_id_param: userId,
      query_embedding_local: localEmbedding,
      query_embedding_openai: openaiEmbedding || Array(1536).fill(0),
      match_count: limit,
      similarity_threshold: threshold
    })

    if (error) {
      isDev && console.error('Error in semantic search:', error)
      throw error
    }

    return (data as ChatSearchResult[]) || []
  } catch (error) {
    isDev && console.error('Error in searchChatsSemantic:', error)
    return []
  }
}
